import { diff, Operation } from 'just-diff';
import index from 'just-index';
import partition from 'just-partition';
import {
  CollectionsFetchDocument,
  CollectionsFetchQuery,
  CollectionUpdateMutationVariables,
  ExternalResourceInput,
  ExternalResourceType,
  useCollectionUpdateMutation,
} from 'lib-knowledge-management-common/graphql';
import { ampli } from 'lib-shared-tracking';

export type DixaResourceInputWithName = ExternalResourceInput & {
  name: string;
};

type GetExternalResourcesReturn = {
  link: ExternalResourceInput[] | undefined;
  unlink: ExternalResourceInput[] | undefined;
};

type GetCollectionUpdateExternalResourcesParams = {
  /** Initial list of resource inputs on first render */
  initialResourceInputs: readonly DixaResourceInputWithName[];
  /**
   * Updated list of resource inputs, generated from the output of
   * <InputSelectionList/>
   */
  currentResourceInputs: DixaResourceInputWithName[];
};

type Difference<T> = {
  op: Operation;
  path: Array<string | number>;
  value: T;
};

const sortByName = (
  a: DixaResourceInputWithName,
  b: DixaResourceInputWithName,
) => {
  return a.name < b.name ? -1 : 1;
};
/**
 * Removes name key so that graphql does not complain about unsupported value
 * being provided.
 */
const removeNameKey = ({
  id,
  type,
}: DixaResourceInputWithName): {
  id: string;
  type: ExternalResourceType;
} => ({ id, type });

export function getExternalResources({
  initialResourceInputs,
  currentResourceInputs,
}: GetCollectionUpdateExternalResourcesParams): GetExternalResourcesReturn {
  // Turn arrays into objects using `index` so `just-diff` only returns add/remove operators.
  // We don't want the replace operator which is returned from array diffing if you pop the 1st element from an array.
  const initial = index(initialResourceInputs, 'id');
  const current = index(currentResourceInputs, 'id');

  // Diff the two objects
  const differences: Difference<DixaResourceInputWithName>[] = diff(
    initial,
    current,
  );

  // Partition the objects between added and removed. Default with empty arrays.
  const [add = [], remove = []] = partition(differences, (difference) => {
    return difference.op === 'add';
  });

  // Create an array of resources to be linked, default to empty array
  const link = add.length
    ? add
        .map((l) => l.value)
        .filter(Boolean)
        .sort(sortByName)
    : [];
  const linkWithoutName = link.map(removeNameKey);

  // Create an array of resources to be unlinked, default to empty array
  const unlink = remove.length
    ? remove
        .reduce((accumulator: DixaResourceInputWithName[], curr) => {
          curr.path.forEach((p) => {
            const pathItem = initial[p];
            accumulator.push(pathItem);
          });
          return accumulator;
        }, [])
        .sort(sortByName)
    : [];
  const unlinkWithoutName = unlink.map(removeNameKey);

  // Don't return empty arrays
  return {
    link: linkWithoutName.length ? linkWithoutName : undefined,
    unlink: unlinkWithoutName.length ? unlinkWithoutName : undefined,
  };
}

type MutationFunction = ReturnType<typeof useCollectionUpdateMutation>[0];
export type CollectionUpdateMutationReturnValue = ReturnType<MutationFunction>;

export type UpdateFn = (
  data: Pick<
    CollectionUpdateMutationVariables,
    'icon' | 'description' | 'internalKbEnabled' | 'internalOnly' | 'title'
  > &
    GetCollectionUpdateExternalResourcesParams,
) => CollectionUpdateMutationReturnValue;

type UseCollectionUpdateReturnValue = [
  UpdateFn,
  ReturnType<typeof useCollectionUpdateMutation>[1],
];

function useCollectionUpdate({
  collectionId,
}: {
  collectionId: string;
}): UseCollectionUpdateReturnValue {
  const [updateMutation, mutationResult] = useCollectionUpdateMutation({
    update: (cache, { data }) => {
      if (data?.collectionUpdate) {
        const collectionList: CollectionsFetchQuery | null = cache.readQuery({
          query: CollectionsFetchDocument,
        });
        if (!collectionList) return;

        const collection = collectionList.collections.results.find(
          (c) => c.id === collectionId,
        );
        if (!collection) return;

        const updatedCollection = {
          ...collection,
          title: data.collectionUpdate.title,
          internalKbEnabled: data.collectionUpdate.internalKbEnabled,
          internalOnly: data.collectionUpdate.internalOnly,
        };
        const updatedCollectionList = {
          ...collectionList,
          collections: {
            ...collectionList.collections,
            results: collectionList.collections.results.map((c) =>
              c.id === collectionId ? updatedCollection : c,
            ),
          },
        };
        cache.writeQuery({
          query: CollectionsFetchDocument,
          data: updatedCollectionList,
        });
      }
    },
  });
  const update: UpdateFn = ({
    title,
    initialResourceInputs,
    currentResourceInputs,
    internalKbEnabled,
    internalOnly,
    description,
    icon,
  }) => {
    const externalResources = getExternalResources({
      initialResourceInputs,
      currentResourceInputs,
    });

    return updateMutation({
      variables: {
        id: collectionId,
        externalResources:
          externalResources.link || externalResources.unlink
            ? externalResources
            : undefined, // No need to provide an externalResources variable if both link and unlink values are undefined
        title,
        internalKbEnabled,
        internalOnly,
        icon,
        description,
      },
    }).then((args) => {
      if (externalResources) {
        if (externalResources.link) {
          externalResources.link.forEach((resource) => {
            ampli.knowledgeCollectionUpdated({
              collectionId,
              resourceId: resource.id,
              action: 'link',
              resourceType: resource.type,
            });
          });
        }
        if (externalResources.unlink) {
          externalResources.unlink.forEach((resource) => {
            ampli.knowledgeCollectionUpdated({
              collectionId,
              resourceId: resource.id,
              action: 'unlink',
              resourceType: resource.type,
            });
          });
        }
      }
      return args;
    });
  };

  return [update, mutationResult];
}

export default useCollectionUpdate;
